<template>
  <div class="department-jobs-settings">
    <v-container fluid>
      <!-- Page Header -->
      <div class="d-flex justify-space-between align-center mb-6">
        <div>
          <h1 class="text-h4 font-weight-bold mb-2">المناصب الإدارية</h1>
          <p class="text-body-1 text-grey">إدارة المناصب والوظائف الإدارية في الأقسام</p>
        </div>
      </div>

      <!-- Content Card -->
      <v-card>
        <v-card-text class="pa-6">
          <div class="d-flex justify-space-between align-center mb-4">
            <h2 class="text-h5">قائمة المناصب الإدارية</h2>
            <v-btn
            v-if="hasPermission('admin.department-jobs.store')"
              v-action="{ module: 'department-jobs', action: 'store' }"
              color="primary"
              prepend-icon="mdi-plus"
              @click="openAddDepartmentJobDialog"
            >
              إضافة منصب جديد
            </v-btn>
          </div>

          <v-data-table
            :headers="departmentJobHeaders"
            :items="departmentJobs"
            :search="departmentJobSearch"
            :loading="loading"
            color="primary"
            first-icon="mdi-chevron-left"
            last-icon="mdi-chevron-right"
            next-icon="mdi-chevron-right"
            prev-icon="mdi-chevron-left"
            show-current-page
            show-first-last-page
            show-first-page
            show-last-page
            show-next-page
            show-previous-page
            class="elevation-1"
          >
            <template #top>
              <v-text-field
                v-model="departmentJobSearch"
                prepend-inner-icon="mdi-magnify"
                label="بحث في المناصب الإدارية"
                variant="outlined"
                density="compact"
                class="mx-4 mt-4"
              ></v-text-field>
            </template>

            <template v-slot:[`item.actions`]="{ item }">
              <v-btn
              v-if="hasPermission('admin.department-jobs.update')"
                v-action="{ module: 'department-jobs', action: 'update' }"
                icon
                size="small"
                color="primary"
                variant="text"
                @click="editDepartmentJob(item)"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn
              v-if="hasPermission('admin.department-jobs.destroy')"
                v-action="{ module: 'department-jobs', action: 'destroy' }"
                icon
                size="small"
                color="error"
                variant="text"
                @click="confirmDelete(item, 'departmentJob')"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Department Job Dialog -->
    <v-dialog v-model="showDepartmentJobDialog" max-width="500px">
      <v-card>
        <v-card-title>
          {{ editingDepartmentJob ? 'تعديل المنصب الإداري' : 'إضافة منصب إداري جديد' }}
        </v-card-title>
        <v-card-text>
          <v-form ref="departmentJobForm" v-model="valid" @submit.prevent="saveDepartmentJob">
            <v-text-field
              v-model="departmentJobForm.name"
              label="اسم المنصب"
              variant="outlined"
              required
              :rules="[v => !!v || 'اسم المنصب مطلوب']"
              class="mb-4"
              placeholder="مدير، نائب مدير، رئيس قسم..."
            ></v-text-field>
            
          
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDepartmentJobDialog = false">إلغاء</v-btn>
          <v-btn color="primary" @click="saveDepartmentJob">حفظ</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="text-h6">تأكيد الحذف</v-card-title>
        <v-card-text>
          هل أنت متأكد من حذف المنصب الإداري؟
          <br><strong>{{ itemToDelete?.name }}</strong>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="deleteDialog = false">إلغاء</v-btn>
          <v-btn color="error" @click="confirmDeleteAction">حذف</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import departmentJobService from '@/services/departmentJobService'
import { hasPermission, hasModule } from '@/utils/permissions'

export default {
  name: 'DepartmentJobsView',
  data() {
    return {
      loading: false,
      valid: false,
      departmentJobSearch: '',
      showDepartmentJobDialog: false,
      editingDepartmentJob: null,
      departmentJobs: [],
      departmentJobForm: {
        name: '',
        desc: '',
        level: 1,
        active: true
      },
      departmentJobHeaders: [
        { title: 'اسم المنصب', key: 'name', align: 'start' },
      
        { title: 'الإجراءات', key: 'actions', align: 'center', sortable: false }
      ],
      deleteDialog: false,
      itemToDelete: null,
      deleteType: ''
    }
  },
  async mounted() {
    await this.loadDepartmentJobs()
  },
  methods: {
    hasPermission(permission) {
      // Prevent permission checks during logout to avoid DOM errors
      if (this.$store.state.isLoggingOut) {
        return false
      }
      return hasPermission(permission)
    },
    
    hasModule(moduleName) {
      // Prevent permission checks during logout to avoid DOM errors
      if (this.$store.state.isLoggingOut) {
        return false
      }
      return hasModule(moduleName)
    },
    async loadDepartmentJobs() {
      this.loading = true
      try {
        const response = await departmentJobService.getDepartmentJobs()
        if (response.data && response.data.data && Array.isArray(response.data.data)) {
          this.departmentJobs = response.data.data
        } else if (response.data && Array.isArray(response.data)) {
          this.departmentJobs = response.data
        } else {
          this.departmentJobs = []
        }
      } catch (error) {
        this.$store.dispatch('alerts/showAlert', {
          type: 'error',
          message: error.message || 'فشل في جلب المناصب الإدارية'
        })
      } finally {
        this.loading = false
      }
    },

    openAddDepartmentJobDialog() {
      this.editingDepartmentJob = null
      this.departmentJobForm = {
        name: '',
        desc: '',
        level: 1,
        active: true
      }
      this.showDepartmentJobDialog = true
    },

    editDepartmentJob(departmentJob) {
      this.editingDepartmentJob = departmentJob
      this.departmentJobForm = {
        name: departmentJob.name,
        desc: departmentJob.desc,
        level: departmentJob.level,
        active: Boolean(departmentJob.active)
      }
      this.showDepartmentJobDialog = true
    },

    async saveDepartmentJob() {
      if (!this.$refs.departmentJobForm.validate()) return

      this.loading = true
      try {
        if (this.editingDepartmentJob) {
          await departmentJobService.updateDepartmentJob(this.editingDepartmentJob.id, this.departmentJobForm)
          this.$store.dispatch('alerts/showAlert', {
            type: 'success',
            message: 'تم تحديث المنصب الإداري بنجاح'
          })
        } else {
          await departmentJobService.createDepartmentJob(this.departmentJobForm)
          this.$store.dispatch('alerts/showAlert', {
            type: 'success',
            message: 'تم إضافة المنصب الإداري بنجاح'
          })
        }
        
        this.showDepartmentJobDialog = false
        await this.loadDepartmentJobs()
      } catch (error) {
        this.$store.dispatch('alerts/showAlert', {
          type: 'error',
          message: error.message || 'فشل في حفظ المنصب الإداري'
        })
      } finally {
        this.loading = false
      }
    },

    confirmDelete(item, type) {
      this.itemToDelete = item
      this.deleteType = type
      this.deleteDialog = true
    },

    async confirmDeleteAction() {
      if (!this.itemToDelete) return

      this.loading = true
      try {
        await departmentJobService.deleteDepartmentJob(this.itemToDelete.id)
        this.$store.dispatch('alerts/showAlert', {
          type: 'success',
          message: 'تم حذف المنصب الإداري بنجاح'
        })
        
        this.deleteDialog = false
        await this.loadDepartmentJobs()
      } catch (error) {
        this.$store.dispatch('alerts/showAlert', {
          type: 'error',
          message: error.message || 'فشل في حذف المنصب الإداري'
        })
      } finally {
        this.loading = false
      }
    },

    getLevelColor(level) {
      const colors = {
        1: 'red',      // Highest level
        2: 'orange',
        3: 'amber',
        4: 'blue',
        5: 'green'     // Lowest level
      }
      return colors[level] || 'grey'
    }
  }
}
</script>

<style scoped>
.department-jobs-settings {
  min-height: 100vh;
}
</style> 